import { useEffect } from "react";
import { Autocomplete, Field, Text, TextField } from "components/commons";
import { ConfirmModal } from "components/modals";
import { useFormContext } from "contexts/form-context";
import locale from "localization";
import styles from "./order-summary-form/order-summary.module.scss";
import { Path } from "enums";

const REJECT_REASON_OPTIONS = [
  { label: "Incomplete Details", value: "Incomplete Details" },
  { label: "Incorrect Quantity", value: "Incorrect Quantity" },
  { label: "Incorrect Pricing", value: "Incorrect Pricing" },
  { label: "Duplicate Request", value: "Duplicate Request" },
  { label: "Others", value: "Others" },
];
const REVISION_REASON_OPTIONS = [
  { label: "Incorrect Item Details", value: "Incorrect Item Details" },
  { label: "Wrong Quantity Entered", value: "Wrong Quantity Entered" },
  { label: "Change in Delivery Date", value: "Change in Delivery Date" },
  { label: "Duplicate Entry", value: "Duplicate Entry" },
  { label: "Others", value: "Others" },
];

const OrderSummaryModal = ({
  modal,
  closable,
  type = "confirm",
  onPrimaryClick,
  items,
  router,
}) => {
  const { fields, modifyField, error } = useFormContext();

  useEffect(() => {
    if (!modal?.isOpen) return;
    if (type === "confirm-reject" || type === "confirm-approval" || type === "confirm-revision") {
      modifyField({ reason: "", reasonRemarks: "" });
    }
  }, [modal?.isOpen, type, modifyField]);

  const renderMessage = (text) => (
    <Text className={styles.message}>{<locale.Populate text={text} items={items} />}</Text>
  );

  let modalProps = {};
  let content = null;

  const isReasonEmpty = !fields.reason.value;
  const isRemarksEmpty = fields.reason.value === "Others" && !fields.reasonRemarks.value.trim();

  switch (type) {
    case "confirm-edit":
      modalProps = {
        title: locale.saveChanges,
        primary: {
          text: locale.saveChanges,
          onClick: () => onPrimaryClick(fields, error),
        },
        secondary: { text: locale.continueEditing },
      };
      content = renderMessage(locale.confirmChanges);
      break;

    case "confirm-approval":
      modalProps = {
        title: locale.confirmApproval,
        primary: { text: locale.approved, onClick: () => onPrimaryClick(fields, error) },
        secondary: { text: locale.cancel },
      };
      content = renderMessage(locale.confirmApprovalMessage);
      break;

    case "confirm-reject":
      modalProps = {
        title: locale.rejectPurchaseOrder,
        primary: {
          text: locale.proceed,
          onClick: () => onPrimaryClick(fields, error),
          disabled: isReasonEmpty || isRemarksEmpty,
        },
        secondary: { text: locale.cancel },
      };
      content = (
        <div className={styles.modal}>
          <Text className={styles.message}>
            {<locale.Populate text={locale.rejectPurchaseOrderMessage} items={items} />}
            <br />
            {locale.inputReasonToProceed}
          </Text>

          <Field
            label={
              <>
                {locale.reason}
                <span style={{ color: "red", marginLeft: "5px" }}>*</span>
              </>
            }
            labelPosition="left-aligned"
            labelClassName={styles.fieldLabelModal}
          >
            <Autocomplete
              {...fields.reason}
              value={REJECT_REASON_OPTIONS.find((opt) => opt.value === fields.reason.value) || null}
              options={REJECT_REASON_OPTIONS}
              onChange={modifyField}
              className={styles.dropdown}
            />
          </Field>

          <Field
            {...fields.reasonRemarks}
            label={locale.remarks}
            labelPosition="left-aligned"
            labelClassName={styles.fieldLabelModal}
          >
            <div className={styles.remarksModal}>
              <TextField
                multiline
                {...fields.reasonRemarks}
                error={fields.reason.value === "Others" && !fields.reasonRemarks.value.trim()}
                onChange={modifyField}
              />
            </div>
          </Field>
        </div>
      );
      break;

    case "confirm-revision":
      modalProps = {
        title: locale.requestRevisionTitle,
        primary: {
          text: locale.proceed,
          onClick: () => onPrimaryClick(fields, error),
          disabled: isReasonEmpty || isRemarksEmpty,
        },
        secondary: { text: locale.cancel },
      };
      content = (
        <div className={styles.modal}>
          <Text className={styles.message}>
            {<locale.Populate text={locale.requestRevisionMessage} items={items} />}
            <br />
            {locale.inputReasonToProceed}
          </Text>

          <Field
            label={
              <>
                {locale.reason}
                <span style={{ color: "red", marginLeft: "5px" }}>*</span>
              </>
            }
            labelPosition="left-aligned"
            labelClassName={styles.fieldLabelModal}
          >
            <Autocomplete
              {...fields.reason}
              value={
                REVISION_REASON_OPTIONS.find((opt) => opt.value === fields.reason.value) || null
              }
              options={REVISION_REASON_OPTIONS}
              onChange={modifyField}
              className={styles.dropdown}
            />
          </Field>

          <Field
            {...fields.reasonRemarks}
            label={locale.remarks}
            labelPosition="left-aligned"
            labelClassName={styles.fieldLabelModal}
          >
            <div className={styles.remarksModal}>
              <TextField
                multiline
                {...fields.reasonRemarks}
                error={fields.reason.value === "Others" && !fields.reasonRemarks.value.trim()}
                onChange={modifyField}
              />
            </div>
          </Field>
        </div>
      );
      break;

    case "success-approval":
      modalProps = {
        title: locale.purchaseOrderApproved,
        primary: { text: locale.gotIt, onClick: () => onPrimaryClick(fields, error) },
      };
      content = renderMessage(locale.purchaseOrderApprovedMessage);
      break;

    case "success-reject":
      modalProps = {
        title: locale.rejectOrderSuccessfully,
        primary: { text: locale.gotIt, onClick: () => onPrimaryClick(fields, error) },
      };
      content = renderMessage(locale.reviseOrderSuccessMessage);
      break;

    case "success-revision":
      modalProps = {
        title: locale.reviseOrderSuccessfully,
        primary: { text: locale.gotIt, onClick: () => onPrimaryClick(fields, error) },
      };
      content = renderMessage(locale.reviseOrderSuccessMessage);
      break;

    case "success-edit":
      modalProps = {
        title: locale.success,
        primary: {
          text: locale.gotIt,
          onClick: () => router.push(Path.OrderSummary),
        },
      };
      content = renderMessage(locale.changesSuccess);
      break;

    default:
      return null;
  }

  return (
    <ConfirmModal {...modal} {...modalProps} closable={closable}>
      <div className={styles.successModal}>{content}</div>
    </ConfirmModal>
  );
};

export default OrderSummaryModal;
